import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle, AlertTriangle, Eye, EyeOff, Lock } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function InviteSetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [name, setName] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/users/invite/${token}/validate`)
      .then(r => { setUser(r.data); setName(r.data.name || ''); setLoading(false); })
      .catch(err => { setError(err.response?.data?.detail || 'Invalid or expired invite link'); setLoading(false); });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/users/invite/${token}/activate`, { password, name });
      setDone(true);
      toast.success('Account activated! Redirecting to login...');
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set password');
    }
    setSubmitting(false);
  };

  if (loading) return (
    <div className="min-h-screen bg-[#F8F6F3] flex items-center justify-center">
      <div className="w-10 h-10 rounded bg-[#0F1D32] animate-pulse" />
    </div>
  );

  if (done) return (
    <div className="min-h-screen bg-[#F8F6F3] flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-8 text-center">
        <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-8 h-8 text-emerald-500" />
        </div>
        <h2 className="text-xl font-bold text-[#0F1D32] mb-2" style={{fontFamily:'Cormorant Garamond', fontStyle:'italic'}}>Account Activated</h2>
        <p className="text-sm text-slate-500">Your password has been set. Redirecting you to login...</p>
      </Card>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#F8F6F3] flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-8 text-center">
        <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-6">
          <AlertTriangle className="w-8 h-8 text-amber-500" />
        </div>
        <h2 className="text-xl font-bold text-[#0F1D32] mb-2" style={{fontFamily:'Cormorant Garamond', fontStyle:'italic'}}>Invite Link Issue</h2>
        <p className="text-sm text-slate-500 mb-4">{error}</p>
        <Button variant="outline" onClick={() => navigate('/login')}>Go to Login</Button>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8F6F3] flex items-center justify-center p-4" data-testid="invite-page">
      <Card className="max-w-md w-full">
        <CardContent className="p-8">
          <div className="text-center mb-8">
            <p className="text-[10px] font-bold tracking-[0.3em] text-[#F59E0B] uppercase">HDH POOLS & SPAS</p>
            <h1 className="text-2xl font-bold text-[#0F1D32] mt-2" style={{fontFamily:'Cormorant Garamond', fontStyle:'italic'}}>Welcome to Pool Ops Hub</h1>
            <p className="text-sm text-slate-500 mt-2">Set your password to get started</p>
          </div>

          <div className="bg-[#F8F6F3] rounded-lg p-4 mb-6 border border-slate-200">
            <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold mb-1">Your Login</p>
            <p className="text-sm font-bold text-[#0F1D32]">{user?.email}</p>
            <p className="text-xs text-slate-400 mt-0.5">{user?.role?.replace('_', ' ')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-xs font-semibold text-slate-700">Your Name</Label>
              <Input value={name} onChange={e => setName(e.target.value)} className="mt-1 h-11" placeholder="Your full name" data-testid="invite-name" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                <Input
                  type={showPw ? 'text' : 'password'}
                  value={password} onChange={e => setPassword(e.target.value)}
                  className="h-11 pl-10 pr-10" placeholder="At least 8 characters"
                  data-testid="invite-password"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-3 text-slate-400 hover:text-slate-600">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700">Confirm Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                <Input
                  type={showPw ? 'text' : 'password'}
                  value={confirm} onChange={e => setConfirm(e.target.value)}
                  className="h-11 pl-10" placeholder="Re-enter password"
                  data-testid="invite-confirm"
                />
              </div>
            </div>
            <Button
              type="submit"
              className="w-full h-12 bg-[#F59E0B] hover:bg-[#D97706] text-[#0F1D32] font-bold text-base rounded-sm"
              disabled={submitting || password.length < 8 || password !== confirm}
              data-testid="invite-submit"
            >
              {submitting ? 'Setting up...' : 'Set Password & Activate Account'}
            </Button>
          </form>

          <p className="text-center text-xs text-slate-400 mt-6">
            Already have an account? <a href="/login" className="text-[#F59E0B] font-semibold hover:underline">Log in</a>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
